import {
  getAdjustedRotation,
  getAircraftCenterOffset,
  getWingspanInPixels,
  MAP_ZOOM_LEVELS,
  MAPINFO_MARKER_TYPE,
} from "../../mapUtils";
import { isBlank, isEmptyList, isNullOrUndefined } from "../../utils";
import PinpointArrow from "../../assets/map/pinpoint-arrow.svg";
import AgentProfilePic from "../../assets/profile-pic-dark.svg";
import AgentEmptyAvailable from "../../assets/map/agent-empty-available.svg";
import Plane from "../../assets/map/plane.svg";
import PlaneMedium from "../../assets/map/plane-medium.svg";
import PlaneLarge from "../../assets/map/plane-large.svg";
import { getSignedImageSrc } from "../../mediaUtils";
import { debugLog } from "../../logging";

export function removeMapMarker(options) {
  const { existingMarker, elementSelector } = options;
  if (!isNullOrUndefined(existingMarker)) {
    try {
      existingMarker.remove();
    } catch (e) {
      debugLog(`Error removing marker: ${e}`);
    }
  }
  if (!isNullOrUndefined(elementSelector)) {
    try {
      // Also try to hard remove any dom elements that didn't get removed
      // This is a workaround for an issue with mapbox's marker.remove()
      const els = document.querySelectorAll(elementSelector);
      if (!isNullOrUndefined(els)) {
        els.forEach((el) => {
          el.remove();
        });
      }
    } catch (e) {
      debugLog(`Error removing marker: ${e}`);
    }
  }
}

export function addMapMarker(mapboxgl, mapRef, options) {
  const { type, markerInfo, clickHandler, mouseoverHandler, mouseoutHandler } =
    options;
  const mapInfo = markerInfo?.mapInfo;
  const markerClass = `marker-image-anchor`;
  const markerImageBoxEl = document.createElement("div");
  markerImageBoxEl.className = markerClass;
  markerImageBoxEl.setAttribute("data-asset-type", type);
  const markerUserPic = document.createElement("div");

  // Generate the proper icon image and content
  const markerImageEl = document.createElement("img");
  if (type === MAPINFO_MARKER_TYPE.VEHICLE) {
    markerImageEl.src = PinpointArrow;
  } else if (type === MAPINFO_MARKER_TYPE.USER) {
    const profileImageBoxEl = document.createElement("div");
    // The main image is just an empty circle with status
    // Users on the map always appear as available (GREEN)
    markerImageEl.src = AgentEmptyAvailable;
    const userProfilePic = getSignedImageSrc(
      markerInfo?.profilePicture?.thumbnail
    );
    // Create an element with the profile pic
    markerUserPic.className = "marker-user-pic";
    markerUserPic.style.position = "relative";
    markerUserPic.style.marginBottom = "2px";
    // Specify the profile image and the default fallback image
    profileImageBoxEl.style.backgroundImage = `url(${userProfilePic}), url(${AgentProfilePic})`;
    profileImageBoxEl.style.width = "18px";
    profileImageBoxEl.style.height = "18px";
    profileImageBoxEl.style.borderRadius = "18px";
    profileImageBoxEl.style.backgroundSize = "cover";
    profileImageBoxEl.style.backgroundPosition = "center";
    profileImageBoxEl.style.overflow = "hidden";

    // Additional styles to overlay the empty circle above the profile pic
    markerImageEl.style.position = "absolute";
    markerImageEl.style.width = "20px";
    markerImageEl.style.height = "20px";
    markerImageEl.style.top = "-1px";
    markerImageEl.style.left = "-1px";

    // Append user pic
    markerUserPic.appendChild(profileImageBoxEl);
  } else if (type === MAPINFO_MARKER_TYPE.AIRCRAFT) {
    const zoom = mapRef.current.getZoom();
    if (zoom > MAP_ZOOM_LEVELS.AIRCRAFT) {
      markerImageEl.src = PlaneLarge;
    } else if (zoom > MAP_ZOOM_LEVELS.AIRPORT) {
      markerImageEl.src = PlaneMedium;
    } else {
      markerImageEl.src = Plane;
    }
    markerImageEl.className = "aircraft-icon";
    markerImageEl.alt = `${markerInfo.registration} (lng: ${mapInfo.position.longitude} lat:${mapInfo.position.latitude})`;
    const aircraftLat = mapInfo.position.latitude;
    const wingspanInPixels = getWingspanInPixels(zoom, aircraftLat);
    markerImageEl.style.width = `${wingspanInPixels}px`;
    markerImageEl.style.height = `${wingspanInPixels}px`;

    // Aircraft has custom rotation handling
    const adjustedRotation = getAdjustedRotation(mapRef, mapInfo);
    markerImageEl.style.transform = `rotate(${adjustedRotation}deg) translate(0, ${
      wingspanInPixels / 2
    }px)`;
  }
  if (
    type !== MAPINFO_MARKER_TYPE.AIRCRAFT &&
    !isNullOrUndefined(mapInfo?.position?.heading)
  ) {
    const adjustedRotation = getAdjustedRotation(mapRef, mapInfo);
    markerImageEl.style.transform = `rotate(${adjustedRotation}deg)`;
  }
  if (!isNullOrUndefined(markerUserPic)) {
    markerImageBoxEl.appendChild(markerUserPic);
  }
  markerImageBoxEl.appendChild(markerImageEl);
  const labelId =
    type === MAPINFO_MARKER_TYPE.AIRCRAFT
      ? markerInfo.registration
      : markerInfo.uuid;

  markerImageEl.classList.add("hoverable");
  markerImageBoxEl.setAttribute("data-anchor-id", labelId);

  // Mouse event handlers
  markerImageEl.addEventListener("mouseover", (e) => {
    if (!isNullOrUndefined(mouseoverHandler)) mouseoverHandler();
  });
  markerImageEl.addEventListener("mouseout", (e) => {
    if (!isNullOrUndefined(mouseoutHandler)) mouseoutHandler();
  });

  // Click handlers
  markerImageEl.addEventListener("click", (e) => {
    clickHandler();
    e.stopPropagation();
  });

  const marker = new mapboxgl.Marker(markerImageBoxEl)
    .setLngLat([mapInfo.position.longitude, mapInfo.position.latitude])
    .setPitchAlignment("map");
  marker.addTo(mapRef.current);
  return marker;
}

export function clearHoverStates() {
  const hoveredEls = document.querySelectorAll(".hovered");
  if (!isEmptyList(hoveredEls)) {
    hoveredEls.forEach((el) => {
      el.classList.remove("hovered");
    });
  }
}

// Handle hover events
export function handleHover(isHovered, markerInfo) {
  const type = markerInfo.mapInfo.markerType;
  const labelId =
    type === MAPINFO_MARKER_TYPE.AIRCRAFT
      ? markerInfo.registration
      : markerInfo.uuid;
  let anchorEl = document.querySelector(`[data-anchor-id='${labelId}']`);
  let labelEl = document.querySelector(`[data-label-id='${labelId}']`);
  let labelElMarker = !isNullOrUndefined(labelEl)
    ? labelEl.closest(".marker-label-anchor")
    : null;

  if (!isNullOrUndefined(labelElMarker)) {
    if (isHovered) {
      labelElMarker.classList.add("hovered");
    } else {
      clearHoverStates();
    }
  }
  if (!isNullOrUndefined(anchorEl)) {
    if (isHovered) {
      anchorEl.classList.add("hovered");
    } else {
      clearHoverStates();
    }
  }
}

export function addLabelForAircraft(mapboxgl, mapRef, options) {
  const { markerInfo, selected, clickHandler } = options;

  const mapInfo = markerInfo?.mapInfo;
  const markerEl = document.createElement("div");
  const markerClass = selected ? "marker selected" : "marker";
  markerEl.className = markerClass;
  const markerLabelBoxEl = document.createElement("div");
  markerLabelBoxEl.setAttribute("data-label-id", markerInfo.registration);

  markerLabelBoxEl.className = "marker-label";

  const markerLabelMainEl = document.createElement("div");
  const markerLabelMainBoxEl = document.createElement("div");
  const markerLabelTextEl = document.createElement("span");
  markerLabelTextEl.textContent = `${mapInfo?.position?.registration}`;
  markerLabelMainBoxEl.appendChild(markerLabelTextEl);

  markerLabelMainEl.appendChild(markerLabelMainBoxEl);
  markerLabelBoxEl.appendChild(markerLabelMainEl);

  // Plane is rotated upwards so adjust label translation
  const zoom = mapRef.current.getZoom();
  const aircraftLat = mapInfo.position.latitude;
  const adjustedRotation = getAdjustedRotation(mapRef, mapInfo);
  const isRotationHorizontal =
    (adjustedRotation > 45 && adjustedRotation < 135) ||
    (adjustedRotation > 225 && adjustedRotation < 315);
  const isRotationUp = adjustedRotation >= 135 && adjustedRotation <= 225;
  const wingspanInPixels = getWingspanInPixels(zoom, aircraftLat);
  const aircraftCenterOffset = getAircraftCenterOffset(
    adjustedRotation,
    wingspanInPixels
  );
  // Add vertical adjustment so that it is always below the aircraft icon
  let adjustmentFactor = zoom > 19 ? 0.65 : zoom > 17 ? 0.6 : 0.55;

  let verticalAdj = isRotationHorizontal
    ? wingspanInPixels * adjustmentFactor
    : isRotationUp
    ? wingspanInPixels
    : wingspanInPixels * adjustmentFactor;

  markerLabelBoxEl.style.transform = `translate(${aircraftCenterOffset.x}px, ${
    aircraftCenterOffset.y * -1 + verticalAdj
  }px)`;

  // Click handlers
  markerLabelBoxEl.addEventListener("click", (e) => {
    if (!isNullOrUndefined(clickHandler)) {
      clickHandler();
    }
    e.stopPropagation();
  });
  markerEl.appendChild(markerLabelBoxEl);

  // Additional wrapper to use as an anchor point
  // The element is 0px by 0px and the actual content overflows outside
  const markerContainer = document.createElement("div");
  const markerContainerClass = `marker-label-anchor${
    selected ? " selected" : ""
  }`;
  markerContainer.className = markerContainerClass;
  markerContainer.appendChild(markerEl);

  const marker = new mapboxgl.Marker(markerContainer)
    .setLngLat([mapInfo.position.longitude, mapInfo.position.latitude])
    .setPitchAlignment("map");

  marker.addTo(mapRef.current);
  return marker;
}

// Use only for Users and Vehicles marker labels
export function addLabelForMarker(mapboxgl, mapRef, options) {
  const {
    type,
    selected,
    markerInfo,
    clickHandler,
    mouseoverHandler,
    mouseoutHandler,
  } = options;
  const mapInfo = markerInfo?.mapInfo;
  const markerEl = document.createElement("div");
  const markerClass = "marker";
  markerEl.className = markerClass;
  const hasTurnaroundContext = !isNullOrUndefined(mapInfo.turnaroundInfo);

  const markerLabelBoxEl = document.createElement("div");

  markerLabelBoxEl.className = "marker-label";

  const markerLabelInnerBoxEl = document.createElement("div");

  if (!isBlank(mapInfo.name)) {
    const markerLabelEl = document.createElement("span");
    markerLabelEl.className = "marker-label-name";
    markerLabelEl.textContent = mapInfo.name;
    markerLabelInnerBoxEl.appendChild(markerLabelEl);

    if (type === MAPINFO_MARKER_TYPE.VEHICLE) {
      markerLabelBoxEl.setAttribute("data-label-id", markerInfo.uuid);
    } else if (type === MAPINFO_MARKER_TYPE.USER) {
      markerLabelBoxEl.setAttribute("data-label-id", markerInfo.uuid);

      if (hasTurnaroundContext && !isNullOrUndefined(mapInfo.operation)) {
        const markerStatusEl = document.createElement("span");
        markerStatusEl.className = `marker-label-status`;
        markerStatusEl.innerText = mapInfo.operation.name;
        markerLabelInnerBoxEl.appendChild(markerStatusEl);
      }
    }
  }
  if (!isBlank(mapInfo.type)) {
    const markerLabelEl = document.createElement("span");
    markerLabelEl.className = "marker-label-type";
    markerLabelEl.textContent = mapInfo.type;
    markerLabelInnerBoxEl.appendChild(markerLabelEl);
  }
  markerLabelBoxEl.appendChild(markerLabelInnerBoxEl);
  markerEl.appendChild(markerLabelBoxEl);

  // Add vertical adjustment so that it is always below the icon
  if (type === MAPINFO_MARKER_TYPE.VEHICLE) {
    markerLabelBoxEl.style.transform = `translateY(25%)`;
  } else {
    markerLabelBoxEl.style.transform = `translateY(75%)`;
  }

  // Click handlers
  markerLabelBoxEl.addEventListener("click", (e) => {
    if (!isNullOrUndefined(clickHandler)) {
      clickHandler();
    }
    e.stopPropagation();
  });

  // Mouse event handlers
  markerLabelBoxEl.addEventListener("mouseover", (e) => {
    if (!isNullOrUndefined(mouseoverHandler)) mouseoverHandler();
  });
  markerLabelBoxEl.addEventListener("mouseout", (e) => {
    if (!isNullOrUndefined(mouseoutHandler)) mouseoutHandler();
  });

  // Additional wrapper to use as an anchor point
  // The element is 0px by 0px and the actual content overflows outside
  const markerContainer = document.createElement("div");
  markerContainer.className = `marker-label-anchor${
    selected ? " selected" : ""
  }`;
  markerContainer.appendChild(markerEl);

  const marker = new mapboxgl.Marker(markerContainer)
    .setLngLat([mapInfo.position.longitude, mapInfo.position.latitude])
    .setPitchAlignment("map");

  marker.addTo(mapRef.current);
  return marker;
}
